import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { CustomValidators } from 'shared/validation/custom-validators';
import * as _ from 'lodash';

import { PasswordValidation } from '../../validation/password-validation';
import { minLengthSpec } from '../../validation/lengthSpec-validation';

import { ProfileInfoService } from '../profile-info.service';


@Component({
  moduleId: module.id,
  selector: 'profile-password-form',
  templateUrl: 'profile-password-form.component.html',
  styleUrls: ['profile-password-form.component.scss'],
})
export class ProfilePasswordFormComponent implements OnInit {
  form: UntypedFormGroup;

  errors: any = {
    incorrect: 'This password is incorrect!',
    notMatch: 'Passwords do not match!',
  };

  matchCurrentPassword: boolean = false;

  @Input() hasCurrentPassword: boolean = true;
  @Input() hasNewPassword: boolean = true;
  @Input() hasNewPasswordConfirm: boolean = true;
  @Input() hasEmail: boolean = false;
  @Input() hasName: boolean = false;
  @Input() nameRequired: boolean = false;
  @Input() showRules: boolean = true;
  @Input() validateEmail: boolean = true;

  @Output() enterClick = new EventEmitter();

  constructor(
    private fb: UntypedFormBuilder,
    private profileInfoService: ProfileInfoService,
  ) {
  }
  // public
  get value() {
    return this.form.value;
  }

  get invalid() {
    return !this.form.valid;
  }

  get currentPasswordPlaceholder() {
    return !this.hasEmail ? 'Please enter your current password' :
      'Password';
  }

  // hooks
  ngOnInit() {
    this.createForm();
  }

  // public
  public setCurrentPasswordNotMatchError() {
    this.form.get('currentPassword').setErrors({notMatchCurrentPassword: true});
  }

  public clearCurrentPasswordErrors() {
    this.form.get('currentPassword').setErrors(null);
  }

  // handlers
  onSubmitClick() {
    if (this.form.invalid) { return; }
    this.profileInfoService.matchCurrentPassword(this.form.get('currentPassword').value)
    .subscribe((status: string) => {
      if (status === 'invalid') {
        this.form.get('currentPassword').setErrors({notMatchCurrentPassword: true});
      } else {
        this.form.get('currentPassword').setErrors(null);
      }
    });
  }

  onEnterClick() {
    this.enterClick.emit();
  }

  // private
  private createForm() {
    const validators = [
      PasswordValidation.SpecialSymbols(),
      minLengthSpec(8),
    ];
    const formGroupEl: any = {};
    if (this.hasCurrentPassword) {
      // without password validations - because current passwords doesn't match them
      formGroupEl.currentPassword = new UntypedFormControl('', minLengthSpec(1));
    }
    if (this.hasNewPassword) {
      formGroupEl.newPassword = new UntypedFormControl('', Validators.compose(validators));
    }
    if (this.hasNewPasswordConfirm) {
      formGroupEl.newPasswordConfirm = new UntypedFormControl('', Validators.compose(validators));
    }
    if (this.hasEmail) {
      if (this.validateEmail) {
        formGroupEl.email = new UntypedFormControl('', Validators.compose([
          CustomValidators.email,
          Validators.required,
        ]));
      } else {
        formGroupEl.email = new UntypedFormControl('', minLengthSpec(1));
      }
    }

    if (this.hasName) {
      if (this.nameRequired) {
        formGroupEl.firstName = new UntypedFormControl('', Validators.compose([
          Validators.required,
        ]));
        formGroupEl.lastName = new UntypedFormControl('', Validators.compose([
          Validators.required,
        ]));
      } else {
        formGroupEl.firstName = new UntypedFormControl('', minLengthSpec(1));
        formGroupEl.lastName = new UntypedFormControl('', minLengthSpec(1));
      }
    }

    // if we have new & confirm passwords - add validations
    const validator = this.hasNewPassword && this.hasNewPasswordConfirm ? {
      validator: this.matchPassword([
        'newPassword',
        'newPasswordConfirm',
      ]),
    } : null;

    this.form = this.fb.group(formGroupEl, validator);
  }

  private matchPassword(fieldNames: Array<string>): ValidatorFn {
    return (ac: AbstractControl): {[key: string]: any} => {
      const newPasswordControl = ac.get('newPassword');
      const newPasswordConfirmControl = ac.get('newPasswordConfirm');
      if (newPasswordControl.value !== newPasswordConfirmControl.value) {
        newPasswordConfirmControl.setErrors({ notMatch: true });
        return { notMatch: true };
      } else {
        if (newPasswordConfirmControl.hasError('notMatch')) {
          let errors =  newPasswordConfirmControl.errors;
          if (errors && errors.notMatch) {
            if (_.keys(errors).length > 1) {
              delete errors.notMatch;
            } else {
              errors = null;
            }
            newPasswordConfirmControl.setErrors(errors);
          }
        }
        return null;
      }
    };
  }

}
